<template>
  <div class="home-page">
    <header class="page-header">
      <h1 class="page-title">Livemusik i Göteborg</h1>
      <p v-if="!isLoading && lastFetch" class="last-fetch">Senast uppdaterad: {{ lastFetch }}</p>
      <div v-else-if="isLoading" class="skeleton skeleton-last-fetch"></div>
    </header>

    <HighlightedCarousel
      city="Göteborg"
      @select="showEventBar"
    />

    <div class="controls">
      <!-- ────────── DESKTOP-LAYOUT (oförändrad) ────────── -->
      <div class="desktop-controls">
        <div class="filter-bar-desktop">
          <span class="filter-label">FILTRERA PÅ PLATS:</span>
          <div class="filter-tabs-group">
            <button
              :class="['filter-tab', venue === '' ? 'active' : '']"
              @click="venue = ''"
            >
              ALLA
            </button>
            <button
              v-for="p in venuesOptions"
              :key="p"
              :class="['filter-tab', venue === p ? 'active' : '']"
              @click="venue = p"
            >
              {{ p.toUpperCase() }}
            </button>
          </div>
        </div>

        <div v-if="showGenreFilter" class="filter-bar-desktop">
          <span class="filter-label">FILTRERA PÅ GENRE:</span>
          <div class="filter-tabs-group">
            <button
              :class="['filter-tab', genre === '' ? 'active' : '']"
              @click="genre = ''"
            >
              ALLA
            </button>
            <button
              v-for="g in availableGenres"
              :key="g"
              :class="['filter-tab', genre === g ? 'active' : '']"
              @click="genre = g"
            >
              {{ g.toUpperCase() }}
            </button>
          </div>
        </div>

        <div class="filter-row">
          <div class="date-range">
            <div class="date-input-wrapper">
              <label class="date-label">FRÅN</label>
              <input
                type="date"
                v-model="dateFrom"
                class="date-input"
                aria-label="Datum från"
              />
            </div>
            <div class="date-input-wrapper">
              <label class="date-label">TILL</label>
              <input
                type="date"
                v-model="dateTo"
                class="date-input"
                aria-label="Datum till"
              />
            </div>
            <button
              v-if="dateFrom || dateTo"
              class="clear-dates"
              @click="dateFrom = ''; dateTo = ''"
              aria-label="Rensa datum"
            >
              ×
            </button>
          </div>

          <div class="search-bar">
            <input
              type="text"
              v-model="search"
              placeholder="Sök event..."
              class="search-input"
              aria-label="Sök event"
            />
            <button
              v-if="search"
              class="search-clear"
              @click="search = ''"
              aria-label="Rensa sökning"
            >
              ×
            </button>
          </div>

          <button
            :class="['compact-toggle', compactMode ? 'compact-toggle--on' : '']"
            @click="compactMode = !compactMode"
            :aria-pressed="compactMode"
            :aria-label="compactMode ? 'Byt till postervy' : 'Byt till listvy'"
          >
            <span class="compact-icon">
              <svg v-if="compactMode" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="2" cy="3" r="1.4" fill="currentColor" />
                <circle cx="2" cy="8" r="1.4" fill="currentColor" />
                <circle cx="2" cy="13" r="1.4" fill="currentColor" />
                <rect x="5" y="2" width="11" height="2" rx="1" fill="currentColor" />
                <rect x="5" y="7" width="11" height="2" rx="1" fill="currentColor" />
                <rect x="5" y="12" width="11" height="2" rx="1" fill="currentColor" />
              </svg>
              <svg v-else width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <rect x="1" y="1" width="6" height="6" stroke="currentColor" stroke-width="1.5" />
                <rect x="9" y="1" width="6" height="6" stroke="currentColor" stroke-width="1.5" />
                <rect x="1" y="9" width="6" height="6" stroke="currentColor" stroke-width="1.5" />
                <rect x="9" y="9" width="6" height="6" stroke="currentColor" stroke-width="1.5" />
              </svg>
            </span>
          </button>
        </div>
      </div>

      <!-- ────────── MOBIL-LAYOUT ────────── -->
      <div class="mobile-controls">
        <!-- Top: search + filter + view toggle -->
        <div class="m-search-row">
          <div class="m-search-bar">
            <svg class="m-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              v-model="search"
              placeholder="Sök event…"
              class="m-search-input"
              aria-label="Sök event"
            />
            <button
              v-if="search"
              class="m-search-clear"
              @click="search = ''"
              aria-label="Rensa sökning"
            >
              ×
            </button>
          </div>

          <button
            class="m-filter-btn"
            :class="{ 'has-filters': activeFilterCount > 0 }"
            @click="showFilterSheet = true"
            aria-label="Öppna filter"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="4" y1="6" x2="20" y2="6"/>
              <line x1="7" y1="12" x2="17" y2="12"/>
              <line x1="10" y1="18" x2="14" y2="18"/>
            </svg>
            <span v-if="activeFilterCount > 0" class="m-filter-count">{{ activeFilterCount }}</span>
          </button>

          <button
            class="m-view-btn"
            @click="compactMode = !compactMode"
            :aria-pressed="compactMode"
            :aria-label="compactMode ? 'Byt till postervy' : 'Byt till listvy'"
          >
            <svg v-if="compactMode" width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="2" cy="3" r="1.4" fill="currentColor" />
              <circle cx="2" cy="8" r="1.4" fill="currentColor" />
              <circle cx="2" cy="13" r="1.4" fill="currentColor" />
              <rect x="5" y="2" width="11" height="2" rx="1" fill="currentColor" />
              <rect x="5" y="7" width="11" height="2" rx="1" fill="currentColor" />
              <rect x="5" y="12" width="11" height="2" rx="1" fill="currentColor" />
            </svg>
            <svg v-else width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <rect x="1" y="1" width="6" height="6" stroke="currentColor" stroke-width="1.5" />
              <rect x="9" y="1" width="6" height="6" stroke="currentColor" stroke-width="1.5" />
              <rect x="1" y="9" width="6" height="6" stroke="currentColor" stroke-width="1.5" />
              <rect x="9" y="9" width="6" height="6" stroke="currentColor" stroke-width="1.5" />
            </svg>
          </button>
        </div>

        <!-- Active filter pills -->
        <div v-if="activeFilters.length" class="m-active-filters">
          <button
            v-for="f in activeFilters"
            :key="f.key"
            class="m-pill"
            @click="f.clear()"
            :aria-label="`Ta bort filter: ${f.label}`"
          >
            {{ f.label }}
            <span class="m-pill-x">×</span>
          </button>
          <button class="m-clear-all" @click="clearAllFilters">Rensa alla</button>
        </div>
      </div>
    </div>

    <!-- ────────── MOBIL FILTER-SHEET ────────── -->
    <div
      v-if="showFilterSheet"
      class="m-sheet-overlay"
      @click="showFilterSheet = false"
    ></div>
    <div class="m-sheet" :class="{ 'm-sheet--open': showFilterSheet }">
      <div class="m-sheet-handle"></div>
      <div class="m-sheet-header">
        <h2 class="m-sheet-title">Filter</h2>
        <button
          class="m-sheet-close"
          @click="showFilterSheet = false"
          aria-label="Stäng filter"
        >
          ×
        </button>
      </div>

      <div class="m-sheet-body">
        <section class="m-section">
          <h3 class="m-section-title">Plats</h3>
          <div class="m-venue-chips">
            <button
              class="m-chip"
              :class="{ 'm-chip--active': venue === '' }"
              @click="venue = ''"
            >
              Alla
            </button>
            <button
              v-for="p in venuesOptions"
              :key="p"
              class="m-chip"
              :class="{ 'm-chip--active': venue === p }"
              @click="venue = p"
            >
              {{ p }}
            </button>
          </div>
        </section>

        <section v-if="showGenreFilter" class="m-section">
          <h3 class="m-section-title">Genre</h3>
          <div class="m-venue-chips">
            <button
              class="m-chip"
              :class="{ 'm-chip--active': genre === '' }"
              @click="genre = ''"
            >
              Alla
            </button>
            <button
              v-for="g in availableGenres"
              :key="g"
              class="m-chip"
              :class="{ 'm-chip--active': genre === g }"
              @click="genre = g"
            >
              {{ g }}
            </button>
          </div>
        </section>

        <section class="m-section">
          <h3 class="m-section-title">Datum</h3>
          <div class="m-date-grid">
            <label class="m-date-field">
              <span class="m-date-label">Från</span>
              <input
                type="date"
                v-model="dateFrom"
                class="m-date-input"
                aria-label="Datum från"
              />
            </label>
            <label class="m-date-field">
              <span class="m-date-label">Till</span>
              <input
                type="date"
                v-model="dateTo"
                class="m-date-input"
                aria-label="Datum till"
              />
            </label>
          </div>
        </section>
      </div>

      <div class="m-sheet-footer">
        <button class="m-btn m-btn--ghost" @click="clearAllFilters">
          Rensa alla
        </button>
        <button class="m-btn m-btn--primary" @click="showFilterSheet = false">
          Visa {{ filteredEvents.length }} event
        </button>
      </div>
    </div>

    <!-- Skeleton loading state -->
    <div v-if="isLoading" class="events-grid">
      <div v-for="n in 9" :key="n" class="skeleton-card">
        <div class="skeleton skeleton-image"></div>
        <div class="skeleton-body">
          <div class="skeleton skeleton-title"></div>
          <div class="skeleton skeleton-title skeleton-title--short"></div>
          <div class="skeleton skeleton-meta"></div>
          <div class="skeleton skeleton-desc"></div>
        </div>
      </div>
    </div>

    <!-- Events grid -->
    <div
      v-else-if="filteredEvents.length"
      :class="compactMode ? 'events-list' : 'events-grid'"
    >
      <EventCard
        v-for="event in filteredEvents"
        :key="event._id || (event.link || '') + event.date"
        :event="event"
        :compact="compactMode"
        @show-event-info="showEventBar"
      />
    </div>

    <!-- No events -->
    <div v-else class="no-events">
      <p>Inga event hittades</p>
    </div>

    <!-- Event info sidebar overlay -->
    <div
      v-if="showEventInfo"
      class="sidebar-overlay"
      @click="closeEventBar"
    ></div>

    <!-- Event info sidebar -->
    <div class="event-sidebar" :class="showEventInfo ? 'show' : ''">
      <event-info
        v-if="showEventInfo"
        :chosen-event="chosenEvent"
        @close="closeEventBar"
      />
    </div>
  </div>
</template>

<script>
import axios from "axios";
import EventInfo from "@/components/event-info.vue";
import EventCard from "@/components/event-card.vue";
import HighlightedCarousel from "@/components/highlighted-carousel.vue";
import { loadFavoriteIds } from "@/utils/favorites";
import { shouldShowGenre } from "@/utils/genreDisplay";
import { applyEventMeta, resetEventMeta } from "@/utils/eventMeta";

export default {
  name: "HomePage",
  components: {
    EventCard,
    EventInfo,
    HighlightedCarousel,
  },
  data() {
    return {
      isLoading: true,
      showEventInfo: false,
      chosenEvent: null,
      search: "",
      venue: "",
      genre: "",
      dateFrom: "",
      dateTo: "",
      venuesOptions: [],
      allEvents: [],
      lastFetch: "",
      compactMode: false,
      showFilterSheet: false,
    };
  },
  computed: {
    // Unika genrer som FAKTISKT visas för aktuell användare. Använder
    // shouldShowGenre så vi (a) returnerar tom lista för utloggade och
    // (b) hoppar över events med låg AI-confidence (de visas inte ändå).
    availableGenres() {
      const set = new Set();
      for (const ev of this.allEvents) {
        if (shouldShowGenre(ev)) set.add(ev.genre);
      }
      return Array.from(set).sort();
    },
    // Filtret ska bara renderas om det finns >1 valbara genrer (annars
    // serverar det ingen filtreringseffekt). Och bara för inloggade —
    // det fångas implicit via availableGenres som returnerar [] för utloggade.
    showGenreFilter() {
      return this.availableGenres.length > 1;
    },
    activeFilters() {
      // Lista över aktiva filter med klickbar X för att rensa
      const arr = [];
      if (this.venue) {
        arr.push({
          key: "venue",
          label: this.venue,
          clear: () => (this.venue = ""),
        });
      }
      if (this.genre) {
        arr.push({
          key: "genre",
          label: this.genre,
          clear: () => (this.genre = ""),
        });
      }
      if (this.dateFrom) {
        arr.push({
          key: "date-from",
          label: `Från ${this.formatShortDate(this.dateFrom)}`,
          clear: () => (this.dateFrom = ""),
        });
      }
      if (this.dateTo) {
        arr.push({
          key: "date-to",
          label: `Till ${this.formatShortDate(this.dateTo)}`,
          clear: () => (this.dateTo = ""),
        });
      }
      return arr;
    },
    activeFilterCount() {
      return this.activeFilters.length;
    },
    filteredEvents() {
      let events = [...this.allEvents];
      if (this.venue) {
        events = events.filter((e) => e.venue === this.venue);
      }
      if (this.genre) {
        // Matcha bara om vi faktiskt skulle visa genren för användaren —
        // annars filtrerar vi bort events där genre är dold (utloggad eller
        // confidence < 0.7), vilket är förvirrande.
        events = events.filter(
          (e) => shouldShowGenre(e) && e.genre === this.genre
        );
      }
      if (this.search) {
        const q = this.search.toLowerCase();
        events = events.filter((e) => e.title.toLowerCase().includes(q));
      }
      if (this.dateFrom) {
        const from = new Date(this.dateFrom).getTime();
        events = events.filter((e) => e._ts >= from);
      }
      if (this.dateTo) {
        const to = new Date(this.dateTo).getTime();
        events = events.filter((e) => e._ts <= to);
      }
      events.sort((a, b) => new Date(a.date) - new Date(b.date));
      return events;
    },
  },
  methods: {
    formatShortDate(iso) {
      try {
        return new Date(iso).toLocaleDateString("sv-SE", {
          day: "numeric",
          month: "short",
        });
      } catch (e) {
        return iso;
      }
    },
    clearAllFilters() {
      this.search = "";
      this.venue = "";
      this.genre = "";
      this.dateFrom = "";
      this.dateTo = "";
    },
    async getEvents() {
      this.isLoading = true;
      try {
        const { data } = await axios.get(
          process.env.VUE_APP_API_URL + "events/gbg"
        );
        const today = new Date().toJSON().split("T")[0];
        this.allEvents = data[0].events
          .filter((event) => !event.title.toLowerCase().includes("inställt"))
          .filter(
            (event) =>
              new Date(event.date.split("T")[0]).getTime() >=
              new Date(today).getTime()
          )
          .map((event) => ({
            ...event,
            _ts: new Date(event.date.split("T")[0]).getTime(),
          }));
        this.venuesOptions = [
          ...new Set(this.allEvents.map((e) => e.venue).filter(Boolean)),
        ];
        // data[0].date = senaste lyckade scrape (max lastSeenAt från
        // backend). Kan vara null om datan saknas — visa inget då.
        // Formateras i svensk lokal tid (API:t skickar UTC).
        this.lastFetch = data[0].date
          ? new Intl.DateTimeFormat("sv-SE", {
              dateStyle: "medium",
              timeStyle: "short",
              timeZone: "Europe/Stockholm",
            }).format(new Date(data[0].date))
          : "";
      } catch (err) {
        console.error("Failed to fetch events:", err.message || err);
      } finally {
        this.isLoading = false;
      }
    },
    showEventBar(event) {
      if (event && event.slug) {
        // URL:en är source of truth — route-watchern öppnar panelen.
        // Delbar länk + bakåtknappen stänger panelen.
        if (this.$route.params.slug !== event.slug) {
          this.$router.push(`/event/${event.slug}`);
        }
      } else {
        // Legacy-event utan slug (backfill ej körd) — öppna utan URL-synk
        this.chosenEvent = event;
        this.showEventInfo = true;
      }
    },
    closeEventBar() {
      if (this.$route.params.slug) {
        // Watchern stänger panelen och återställer meta när routen blir "/"
        this.$router.push("/");
      } else {
        this.showEventInfo = false;
      }
    },
    /**
     * Synkar event-panelen mot :slug i URL:en. Körs vid route-byten och
     * efter att event-listan laddats (direktbesök på /event/:slug).
     *
     * Eventet hämtas i första hand från den redan laddade listan; faller
     * tillbaka på /api/events/by-slug/:slug för events som inte finns i
     * listan (passerade/inaktiva — deras sidor ska leva kvar).
     */
    async syncPanelFromRoute() {
      const slug = this.$route.params.slug;

      if (!slug) {
        this.showEventInfo = false;
        resetEventMeta();
        return;
      }

      let event = this.allEvents.find((e) => e.slug === slug);

      if (!event) {
        try {
          const { data } = await axios.get(
            process.env.VUE_APP_API_URL + "events/by-slug/" + slug
          );
          event = data;
        } catch (err) {
          event = { notFound: true };
        }
      }

      this.chosenEvent = event;
      this.showEventInfo = true;
      if (!event.notFound) applyEventMeta(event);
    },
  },
  watch: {
    // Panelen styrs helt av URL:en: öppna vid /event/:slug, stäng vid "/"
    "$route.params.slug"() {
      // Bara när HomePage-instansen fortfarande är aktiv (route "/" eller
      // "/event/:slug" — samma komponent, Vue återanvänder instansen)
      if (this.$route.name === "home" || this.$route.name === "concert") {
        this.syncPanelFromRoute();
      }
    },
  },
  async created() {
    // Ladda parallellt - favorit-state behövs för hjärt-ikonerna
    await Promise.all([this.getEvents(), loadFavoriteIds()]);
    // Direktbesök på /event/:slug — öppna panelen när datan finns
    if (this.$route.params.slug) {
      await this.syncPanelFromRoute();
    }
  },
  unmounted() {
    resetEventMeta();
  },
};
</script>

<style scoped>
.home-page {
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 2rem 5rem;
}

/* Header */
.page-header {
  margin-bottom: 2.5rem;
}

.page-title {
  font-family: "Playfair Display", serif;
  font-weight: 900;
  font-size: clamp(2.5rem, 7vw, 6rem);
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  color: var(--color-text);
  margin-bottom: 0.5rem;
}

.last-fetch {
  font-size: 0.7rem;
  color: var(--color-text-faint);
  letter-spacing: 0.05em;
}

/* Controls */
.controls {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2.5rem;
  border-top: 1px solid var(--color-border);
  padding-top: 1.25rem;
}

/* Desktop filter tabs */
/* Två-kolumns-grid: label i vänster kolumn (fixed width), chips i höger.
   Detta gör att när chipsen wrappar (t.ex. när det finns många genrer)
   så hamnar nästa rad i CHIPS-kolumnen — alignad med första chip:en —
   istället för att börja om på vänsterkanten under labeln. */
.filter-bar-desktop {
  display: grid;
  grid-template-columns: 12rem 1fr;
  align-items: start;
  column-gap: 0;
}

/* Andra filter-raden får extra luft uppåt så plats- och genre-raderna inte
   ser ut som ett block. Adjacent-sibling triggar automatiskt om vi
   lägger till fler filter-rader senare. */
.filter-bar-desktop + .filter-bar-desktop {
  margin-top: 1rem;
}

.filter-label {
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--color-text-faint);
  white-space: nowrap;
  /* Linjera vertikalt med första chip-raden (filter-tab har padding 0.2rem 0
     + line-height ~1.2, så vi nudge:ar labeln nedåt minimalt). */
  padding-top: 0.2rem;
}

/* Wrapper runt chipsen så de wrappar inom sin egen kolumn. */
.filter-tabs-group {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 0rem; /* row-gap, column-gap */
  align-items: center;
}

.filter-tab {
  background: none;
  border: none;
  padding: 0.2rem 0;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  color: var(--color-text-secondary);
  transition: color 0.15s ease;
  margin-right: 1.25rem;
  border-bottom: 2px solid transparent;
  line-height: 1.6;
}

.filter-tab:hover {
  color: var(--color-accent);
}

.filter-tab.active {
  color: var(--color-accent);
  border-bottom-color: var(--color-accent);
}

/* Mobil-blocket - dolt på desktop */
.mobile-controls {
  display: none;
}

/* Date range + search row */
.filter-row {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  /* Luft mellan genre-/plats-filter ovanför och datum/sök-raden nedanför. */
  margin-top: 0.5rem;
}

.date-range {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.date-input-wrapper {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.date-label {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
  white-space: nowrap;
}

.date-input {
  border: none;
  border-bottom: 1px solid var(--color-border-light);
  background: transparent;
  padding: 0.3rem 0;
  font-size: 0.78rem;
  color: var(--color-input-text);
  outline: none;
  transition: border-color 0.15s ease;
  cursor: pointer;
}

.date-input::-webkit-calendar-picker-indicator {
  cursor: pointer;
  opacity: 0.8;
}

.date-input:focus {
  border-bottom-color: var(--color-text);
}

.clear-dates {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  color: var(--color-text-faint);
  line-height: 1;
  padding: 0;
  transition: color 0.15s ease;
}

.clear-dates:hover {
  color: var(--color-accent);
}

/* Search */
.search-bar {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  max-width: 280px;
}

.search-input {
  width: 100%;
  border: none;
  border-bottom: 1px solid var(--color-border-light);
  background: transparent;
  padding: 0.4rem 2rem 0.4rem 0;
  font-size: 0.8rem;
  color: var(--color-input-text);
  outline: none;
  transition: border-color 0.15s ease;
  letter-spacing: 0.03em;
}

.search-input::placeholder {
  color: var(--color-text-secondary);
  font-size: 0.78rem;
  letter-spacing: 0.06em;
}

.search-input:focus {
  border-bottom-color: var(--color-text);
}

.search-clear {
  position: absolute;
  right: 0;
  background: none;
  border: none;
  font-size: 1.3rem;
  cursor: pointer;
  color: var(--color-text-faint);
  line-height: 1;
  padding: 0;
  transition: color 0.15s ease;
}

.search-clear:hover {
  color: var(--color-accent);
}

/* Compact toggle */
.compact-toggle {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: none;
  border: none;
  padding: 0.3rem 0.65rem;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  color: var(--color-text-muted);
  transition: color 0.15s ease;
  white-space: nowrap;
  flex-shrink: 0;
}

.compact-toggle:hover {
  color: var(--color-text);
}

.compact-toggle--on {
  color: var(--color-text-muted);
}

.compact-icon {
  display: flex;
  align-items: center;
}

.compact-icon svg {
  width: 20px;
  height: 20px;
}

/* Skeleton loading */
@keyframes shimmer {
  0% { background-position: -600px 0; }
  100% { background-position: 600px 0; }
}

.skeleton {
  background: linear-gradient(
    90deg,
    var(--color-skeleton-base) 25%,
    var(--color-skeleton-highlight) 50%,
    var(--color-skeleton-base) 75%
  );
  background-size: 1200px 100%;
  animation: shimmer 1.6s infinite linear;
  border-radius: 2px;
}

.skeleton-last-fetch {
  width: 14rem;
  height: 0.75rem;
  margin-top: 0.4rem;
}

.skeleton-card {
  display: flex;
  flex-direction: column;
}

.skeleton-image {
  width: 100%;
  aspect-ratio: 4 / 5;
  border-radius: 2px;
}

.skeleton-body {
  padding: 0.75rem 0.25rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.skeleton-title {
  height: 1rem;
  width: 90%;
}

.skeleton-title--short {
  width: 55%;
}

.skeleton-meta {
  height: 0.7rem;
  width: 70%;
}

.skeleton-desc {
  height: 0.7rem;
  width: 85%;
}

/* Events grid */
.events-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2.5rem 2rem;
}

/* Compact list */
.events-list {
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--color-border);
}

/* No events */
.no-events {
  text-align: center;
  padding: 5rem 2rem;
  color: var(--color-text-faint);
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.15em;
}

/* Sidebar overlay */
.sidebar-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 999;
}

/* Event info sidebar */
.event-sidebar {
  position: fixed;
  left: -480px;
  top: 0;
  width: 440px;
  height: 100vh;
  z-index: 1000;
  transition: left 0.3s ease;
  overflow-y: auto;
}

.event-sidebar.show {
  left: 0;
}

/* Responsive */
@media (max-width: 1100px) {
  .events-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 700px) {
  .home-page {
    padding: 2rem 1rem 4rem;
  }

  .controls {
    margin-bottom: 1rem;
    padding-top: 0;
    border-top: none;
    gap: 0.85rem;
  }

  .events-grid {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  /* Dölj desktop-kontroller på mobil */
  .desktop-controls {
    display: none;
  }

  .mobile-controls {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
  }

  .event-sidebar {
    width: 100%;
    left: -100%;
  }

  .event-sidebar.show {
    left: 0;
  }
}

/* ─────────────────────────────────────────────
   Mobile controls
   ───────────────────────────────────────────── */

.m-search-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.m-search-bar {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  background: var(--color-bg-soft, rgba(0, 0, 0, 0.04));
  border: 1px solid var(--color-border-light);
  border-radius: 8px;
  padding: 0 0.7rem;
  height: 42px;
  transition: border-color 0.15s ease;
}

.m-search-bar:focus-within {
  border-color: var(--color-text);
}

.m-search-icon {
  color: var(--color-text-secondary);
  flex-shrink: 0;
  margin-right: 0.5rem;
}

.m-search-input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 16px; /* förhindrar iOS-zoom */
  color: var(--color-input-text);
  height: 100%;
  padding: 0;
  min-width: 0;
}

.m-search-input::placeholder {
  color: var(--color-text-secondary);
}

.m-search-clear {
  background: none;
  border: none;
  font-size: 1.25rem;
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: 0 0.25rem;
  line-height: 1;
}

.m-filter-btn,
.m-view-btn {
  position: relative;
  width: 42px;
  height: 42px;
  border-radius: 8px;
  border: 1px solid var(--color-border-light);
  background: var(--color-bg);
  color: var(--color-text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.m-filter-btn:hover,
.m-view-btn:hover,
.m-filter-btn:active,
.m-view-btn:active {
  border-color: var(--color-text);
  color: var(--color-text);
}

.m-filter-btn.has-filters {
  border-color: var(--color-accent);
  color: var(--color-accent);
}

.m-filter-count {
  position: absolute;
  top: -4px;
  right: -4px;
  background: var(--color-accent);
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.m-chip {
  flex-shrink: 0;
  padding: 0.45rem 0.9rem;
  border-radius: 999px;
  border: 1px solid var(--color-border-light);
  background: var(--color-bg);
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  white-space: nowrap;
  text-transform: capitalize;
}

.m-chip:hover {
  border-color: var(--color-text-secondary);
}

.m-chip--active {
  background: var(--color-text);
  color: var(--color-bg);
  border-color: var(--color-text);
}

/* Active filter pills */
.m-active-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
}

.m-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  background: var(--color-accent);
  color: #fff;
  border: none;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  letter-spacing: 0.02em;
  text-transform: capitalize;
}

.m-pill-x {
  font-size: 1rem;
  line-height: 0.6;
  margin-left: 0.1rem;
}

.m-clear-all {
  background: none;
  border: none;
  color: var(--color-text-secondary);
  font-size: 0.75rem;
  text-decoration: underline;
  cursor: pointer;
  padding: 0.25rem 0.4rem;
}

/* ─────────────────────────────────────────────
   Filter sheet (slide-up bottom drawer)
   ───────────────────────────────────────────── */

.m-sheet-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 998;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.m-sheet {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  background: var(--color-bg);
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.18);
  transform: translateY(100%);
  visibility: hidden;
  pointer-events: none;
  transition: transform 0.28s cubic-bezier(0.32, 0.72, 0, 1),
    visibility 0s linear 0.28s;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
}

.m-sheet--open {
  transform: translateY(0);
  visibility: visible;
  pointer-events: auto;
  transition: transform 0.28s cubic-bezier(0.32, 0.72, 0, 1), visibility 0s;
}

.m-sheet-handle {
  width: 40px;
  height: 4px;
  background: var(--color-border);
  border-radius: 999px;
  margin: 12px auto 4px;
  flex-shrink: 0;
}

.m-sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 1.25rem 0.4rem;
  flex-shrink: 0;
}

.m-sheet-title {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
}

.m-sheet-close {
  background: none;
  border: none;
  font-size: 1.75rem;
  color: var(--color-text-secondary);
  cursor: pointer;
  line-height: 1;
  padding: 0.25rem 0.5rem;
}

.m-sheet-body {
  padding: 0.5rem 1.25rem 1rem;
  overflow-y: auto;
  flex: 1;
}

.m-section {
  margin-bottom: 1.5rem;
}

.m-section-title {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
  margin: 0 0 0.75rem;
}

.m-venue-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.m-date-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.m-date-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.m-date-label {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
}

.m-date-input {
  height: 44px;
  border: 1px solid var(--color-border-light);
  border-radius: 8px;
  padding: 0 0.75rem;
  background: var(--color-bg);
  font-size: 16px;
  color: var(--color-input-text);
  outline: none;
}

.m-date-input:focus {
  border-color: var(--color-text);
}

.m-sheet-footer {
  display: flex;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--color-border);
  flex-shrink: 0;
}

.m-btn {
  flex: 1;
  height: 44px;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s ease;
}

.m-btn--ghost {
  background: transparent;
  border: 1px solid var(--color-border);
  color: var(--color-text-secondary);
}

.m-btn--primary {
  background: var(--color-text);
  color: var(--color-bg);
  flex: 2;
}

.m-btn:active {
  opacity: 0.8;
}
</style>

